// src/data/schoolByod.ts
// -----------------------
// Repository for the `school_byod` band table (db-198). One active row per policy
// band, each with its own year range + provenance + check date. Internal-read only
// (RLS: staff/admin/picker/combined); writes go through the byod-lookup Edge
// Function (service role), never from here. Typed via getTypedClient().

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type ByodBand = Database["public"]["Tables"]["school_byod"]["Row"];

// The ACTIVE bands for a school (deactivated rows excluded), ordered by year_from.
// [] on error / no client / no school. Resolving which band covers a child's year
// is done client-side by resolveCoveringBand (src/services/byodBands.ts).
export async function listActiveBandsForSchool(schoolId: number): Promise<ByodBand[]> {
  const c = getTypedClient();
  if (!c) return [];
  const { data, error } = await c
    .from("school_byod")
    .select("*")
    .eq("school_id", schoolId)
    .eq("deactivated", false)
    .order("year_from");
  if (error) {
    logger.warn("listActiveBandsForSchool failed:", error.message);
    return [];
  }
  return (data ?? []) as ByodBand[];
}

// Which of the given school ids have at least one ACTIVE BYOD band (db-198).
// Powers the "BYOD Policy" column on the admin Schools list: rather than N band
// queries, one `.in(...)` over the page's ids returns a set to mark rows against.
// Empty set on error / no client / no ids.
export async function listSchoolIdsWithByod(schoolIds: number[]): Promise<Set<number>> {
  const c = getTypedClient();
  if (!c || schoolIds.length === 0) return new Set();
  const { data, error } = await c
    .from("school_byod")
    .select("school_id")
    .in("school_id", schoolIds)
    .eq("deactivated", false);
  if (error) {
    logger.warn("listSchoolIdsWithByod failed:", error.message);
    return new Set();
  }
  return new Set(((data ?? []) as Array<{ school_id: number }>).map((r) => r.school_id));
}
